import React, { useEffect, useRef } from "react";
import gsap from "gsap";

const COLORS = {
  primary: "#3AAFA9",
  secondary: "#7BC8A4",
  heading: "#2E2E2E",
  text: "#4B5563",
  sectionTitle: "#1F7A75",
  background: "#3AAFA9",
};

const disclaimerSections = [
  {
    title: "1. Medical Disclaimer",
    text:
      "JANAK ELDERBRIDGE PRIVATE LIMITED (“Janak”) provides a platform for care coordination, basic health monitoring, and daily living support. We do not provide medical advice, diagnosis, or clinical treatment. The services and information offered on this platform should not be construed as a substitute for professional medical care by a qualified doctor or healthcare provider.",
  },
  {
    title: "2. No Guarantee of Outcomes",
    text:
      "Janak strives to provide reliable, high-quality care support. However, we do not guarantee specific health outcomes, improvements, or complete prevention of medical complications. Our care partners act as support aides and not as registered medical practitioners, unless explicitly stated otherwise for a specific service.",
  },
  {
    title: "3. Third-Party Links and Services",
    text:
      "Our platform may contain links to third-party healthcare providers, diagnostic centers, or pharmacies. We do not endorse or assume liability for the quality, accuracy, or safety of services provided by these independent third parties.",
  },
  {
    title: "4. Fitness for Use",
    text:
      "The Janak App and website are provided on an 'as-is' and 'as-available' basis. We make no representations or warranties, express or implied, regarding the continuous, uninterrupted, or error-free operation of our digital platforms.",
  },
  {
    title: "5. Professional Advice",
    text:
      "Always seek the advice of your physician or other qualified health provider with any questions you may have regarding a medical condition. Never disregard professional medical advice or delay in seeking it because of something you have read or experienced on the Janak platform.",
  },
  {
    title: "6. Contact Information",
    text: (
      <span>
        For any queries regarding this disclaimer, please contact us at{" "}
        <a href="mailto:contact@thejanak.com" className="text-[#3AAFA9] hover:underline font-medium">
          contact@thejanak.com
        </a>{" "}
        or call{" "}
        <a href="tel:+919098114568" className="text-[#3AAFA9] hover:underline font-medium">
          +91 90981 14568
        </a>.
      </span>
    ),
  },
];

const Disclaimer = () => {
  const sectionRef = useRef(null);
  const cardRef = useRef(null);
  const headingRef = useRef(null);
  const introRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
      tl.from(sectionRef.current, { opacity: 0, duration: 0.5 })
        .from(cardRef.current, { y: 36, opacity: 0, duration: 0.7 }, "-=0.2")
        .from(headingRef.current, { y: 18, opacity: 0, duration: 0.45 }, "-=0.2")
        .from(introRef.current, { y: 16, opacity: 0, duration: 0.45 }, "-=0.25")
        .from(
          ".disclaimer-section-item",
          { y: 24, opacity: 0, stagger: 0.07, duration: 0.38 },
          "-=0.2",
        );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen overflow-hidden px-6 py-20"
      style={{ background: COLORS.background }}
    >
      <div
        className="absolute left-10 top-24 h-72 w-72 rounded-full blur-3xl"
        style={{ background: `${COLORS.primary}33` }}
      />
      <div
        className="absolute bottom-20 right-10 h-72 w-72 rounded-full blur-3xl"
        style={{ background: `${COLORS.secondary}40` }}
      />

      <div
        ref={cardRef}
        className="relative mx-auto max-w-4xl rounded-3xl bg-white/80 px-6 py-12 text-gray-800 shadow-[0_30px_90px_rgba(0,0,0,0.15)] backdrop-blur-2xl md:px-12"
      >
        <h1
          ref={headingRef}
          className="mb-10 text-center text-4xl font-extrabold md:text-5xl"
          style={{ color: COLORS.heading }}
        >
          Disclaimer
        </h1>

        <p
          ref={introRef}
          className="mb-10 text-center text-base leading-relaxed md:text-lg"
          style={{ color: COLORS.text }}
        >
          Please review the following important information regarding the scope and limitations of the services provided by Janak.
        </p>

        <div className="space-y-9 text-[15.5px] leading-relaxed">
          {disclaimerSections.map((section) => (
            <div key={section.title} className="disclaimer-section-item">
              <h2
                className="mb-3 text-xl font-bold md:text-2xl"
                style={{ color: COLORS.sectionTitle }}
              >
                {section.title}
              </h2>
              {section.text ? <p style={{ color: COLORS.text }}>{section.text}</p> : null}
              {section.list ? (
                <ul className="list-disc space-y-1.5 pl-6" style={{ color: COLORS.text }}>
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ))}
        </div>

        <p className="mt-14 text-center text-sm text-gray-500">Last updated: July 2026</p>
      </div>
    </section>
  );
};

export default Disclaimer;
